import { useNavigate } from 'react-router-dom';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function OfficerDashboard({ data }) {
  const navigate = useNavigate();
  const sites = data?.site_oversight || [];
  const evaluations = data?.pending_evaluations || [];
  const promotions = data?.promotion_recommendations || [];

  return (
    <div className="space-y-6" data-testid="officer-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Officer Dashboard</h1>
        <p className="text-gray-400">Operations oversight and personnel management</p>
      </div>

      {/* Site Oversight */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">Site Oversight</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map(site => (
            <SiteCard key={site.site_id} site={site} />
          ))}
        </div>
        <button onClick={() => navigate('/deployments')} className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white text-sm">
          Manage Sites
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Evaluations */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Pending Evaluations</h2>
          {evaluations.length > 0 ? (
            <div className="space-y-3">
              {evaluations.slice(0, 5).map(ev => (
                <div key={ev.enrollment_id} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">{ev.user_name}</p>
                      <p className="text-gray-400 text-sm">{ev.service_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-400 text-sm font-medium">{ev.program_code}</p>
                      <p className="text-gray-500 text-xs">Started {formatDate(ev.start_date)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">No pending evaluations</p>
          )}
          <button onClick={() => navigate('/training')} className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm">
            View All Training
          </button>
        </div>

        {/* Promotion Recommendations */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">Promotion Recommendations</h2>
          {promotions.length > 0 ? (
            <div className="space-y-3">
              {promotions.slice(0, 5).map(rec => (
                <div key={rec.request_id} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">{rec.user_name}</p>
                      <p className="text-gray-400 text-sm">{rec.current_rank} → {rec.target_rank}</p>
                    </div>
                    <StatusBadge status={rec.status} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-4">No pending recommendations</p>
          )}
          <button onClick={() => navigate('/promotions')} className="mt-4 w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white text-sm">
            View All Promotions
          </button>
        </div>
      </div>
    </div>
  );
}

function SiteCard({ site }) {
  const riskColors = {
    Critical: 'border-l-red-500',
    High: 'border-l-orange-500',
    Medium: 'border-l-yellow-500',
    Low: 'border-l-green-500'
  };
  const borderClass = riskColors[site.risk_classification] || riskColors.Low;

  return (
    <div className={`bg-gray-700 rounded-lg p-4 border-l-4 ${borderClass}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white font-medium">{site.name}</p>
          <p className="text-gray-400 text-sm truncate max-w-[200px]">{site.address || 'No address'}</p>
        </div>
        {site.active_incidents > 0 && (
          <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-medium">
            {site.active_incidents} incidents
          </span>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="text-gray-400">{site.team_count} teams</span>
        <span className={`px-2 py-0.5 rounded text-xs ${
          site.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
        }`}>{site.status}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    supervisor_approved: 'bg-blue-500/20 text-blue-400',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400'
  };
  const colorClass = colors[status] || colors.pending;
  const displayText = status.replace('_', ' ');

  return (
    <span className={`px-2 py-1 rounded text-xs ${colorClass}`}>{displayText}</span>
  );
}

export default OfficerDashboard;
